import { css, type Theme } from '@emotion/react';
import styled from '@emotion/styled';
import { tint } from 'polished';
import { LoadingCircle as RawLoadingCircle } from '../../Ui';

type ButtonComponentProps = {
  iconAlignment: 'left' | 'right';
  isDisabled: boolean;
  isSecondaryButton: boolean;
  isSmallButton: boolean;
  isFullWidth: boolean;
};

const LoadingCircle = styled(RawLoadingCircle)`
  position: absolute;
  padding: 2px;
  width: 1.25rem;
  height: 1.25rem;

  &::after {
    border-width: 2px;
    border-color: currentcolor;
    border-left-color: transparent;
  }
`;

const HiddenText = styled.span`
  display: inherit;
  visibility: hidden;
`;

const getPrimaryStyles = (
  defaultColor: string,
  highlightColor: string,
  theme: Theme,
) => css`
  color: ${theme.colors.neutral.white100};
  background-color: ${defaultColor};
  transition: background 1s;

  @media (hover: hover) {
    &:hover {
      background-color: ${highlightColor};
      background-image: radial-gradient(
        circle,
        transparent 1%,
        ${highlightColor} 1%
      );
      background-position: center;
      background-size: 15000%;
    }
  }

  &:active {
    background-color: ${tint(0.15, highlightColor)};
    background-size: 100%;
    transition: background 0s;
  }
`;

const getPrimaryDisabledStyles = (theme: Theme) => css`
  color: ${theme.colors.neutral.grey80};
  background-color: ${theme.colors.neutral.grey20};
`;

const getSecondaryStyles = (
  defaultColor: string,
  highlightColor: string,
  isSmallButton: boolean,
  theme: Theme,
) => css`
  padding: ${isSmallButton ? '0.5rem 0.75rem' : '0.875rem 1.875rem'};
  border: 2px solid currentcolor;
  color: ${defaultColor};
  background-color: ${theme.colors.neutral.white100};
  transition-property: color, background, border-color;
  transition-duration: 1s;

  @media (hover: hover) {
    &:hover {
      color: ${highlightColor};
      background-color: ${theme.colors.neutral.white100};
      background-image: radial-gradient(
        circle,
        transparent 1%,
        ${theme.colors.neutral.white100} 1%
      );
      background-position: center;
      background-size: 15000%;
    }
  }

  &:active {
    color: ${highlightColor};
    background-color: ${tint(0.8, highlightColor)};
    background-size: 100%;
    transition: background 0s;
  }
`;

const getSecondaryDisabledStyles = (
  isSmallButton: boolean,
  theme: Theme,
) => css`
  padding: ${isSmallButton ? '0.5rem 0.875rem' : '0.875rem 1.875rem'};
  border: 2px solid ${theme.colors.neutral.grey40};
  color: ${theme.colors.neutral.grey80};
  background-color: ${theme.colors.neutral.white100};
`;

const ButtonComponent = styled.button<ButtonComponentProps>`
  display: ${({ isFullWidth }) => (isFullWidth ? 'flex' : 'inline-flex')};
  flex-direction: ${({ iconAlignment }) =>
    iconAlignment === 'right' ? 'row-reverse' : ''};
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  padding: ${({ isSmallButton }) =>
    isSmallButton ? '0.625rem 1rem' : '1rem 2rem'};
  border: 0;
  outline: 0;
  border-radius: ${({ theme }) => theme.radius.sm};
  overflow: visible;
  box-sizing: border-box;
  width: ${({ isFullWidth }) => (isFullWidth ? '100%' : '')};
  max-width: 100%;
  white-space: nowrap;
  text-decoration: none;
  text-transform: none;
  fill: currentcolor;
  background-position: center;
  -webkit-tap-highlight-color: transparent;
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
  user-select: none;
  appearance: none;

  &:focus-visible {
    outline: 0.125rem dashed ${({ theme }) => theme.colors.brand.orange100};
    outline-offset: 0.25rem;
  }

  ${({ theme, isSecondaryButton, isSmallButton, isDisabled }) => {
    const defaultColor = theme.colors.brand.green120;
    const highlightColor = theme.colors.brand.green100;

    if (isSecondaryButton) {
      if (isDisabled) {
        return getSecondaryDisabledStyles(isSmallButton, theme);
      }

      return getSecondaryStyles(
        defaultColor!,
        highlightColor!,
        isSmallButton,
        theme,
      );
    }

    if (isDisabled) {
      return getPrimaryDisabledStyles(theme);
    }

    return getPrimaryStyles(defaultColor!, highlightColor!, theme);
  }};
`;

export { ButtonComponent, LoadingCircle, HiddenText };
